import React from 'react';
import { useQuery } from 'react-query';
import { AlertTriangle, Link2, Loader2 } from 'lucide-react';
import objectiveService from '../../services/objectiveService';
import { documentApi } from '../../services/api';
import { CausationAnalysisPanel } from './CausationAnalysisPanel';
import { DerivedOutputBanner } from './DerivedOutputBanner';

interface CausationAnalysisTabProps {
  caseId: string;
}

export const CausationAnalysisTab: React.FC<CausationAnalysisTabProps> = ({ caseId }) => {
  const { data: objectives = [] } = useQuery(
    ['objectives', caseId],
    () => objectiveService.getObjectives(caseId),
    {
      enabled: !!caseId,
      refetchInterval: (data) => {
        const obj = data?.find(o => o.type === 'CAUSATION_ANALYSIS');
        return obj?.status === 'PROCESSING' ? 2000 : false;
      },
    }
  );

  const objective = objectives.find(o => o.type === 'CAUSATION_ANALYSIS');

  const { data: output } = useQuery(
    ['output', caseId, objective?.id],
    () => objectiveService.getOutput(caseId, objective!.id),
    { enabled: !!objective && objective.status === 'UP_TO_DATE' && !!objective.lastGeneratedAt, retry: false }
  );

  const { data: documents = [] } = useQuery(
    ['documents', caseId],
    () => documentApi.getCaseDocuments(caseId),
    { enabled: !!caseId }
  );

  const isProcessing = objective?.status === 'PROCESSING';

  return (
    <div className="p-6 h-full overflow-auto space-y-6">
      <div className="mb-2">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-medical-100 mb-1">Causation Analysis</h3>
        <p className="text-sm text-gray-600 dark:text-medical-400">
          Analyse causal relationships between clinical events and patient outcomes.
        </p>
      </div>

      <DerivedOutputBanner
        caseId={caseId}
        analysisLabel="causation analysis"
        evaluatePrompt="I have corrected some chronology events since the causation analysis was generated. Review the edited events and assess what bearing the corrections have on the causation analysis — are the causal links, chain of events, or conclusions affected?"
      />

      {objective && objective.status === 'UP_TO_DATE' && output ? (
        <CausationAnalysisPanel content={output.content} documents={documents} />
      ) : isProcessing ? (
        <div className="text-center py-12">
          <Loader2 className="w-10 h-10 text-cyan-500 mx-auto mb-3 animate-spin" />
          <p className="text-sm text-gray-600 dark:text-medical-400">Causation analysis is being generated...</p>
        </div>
      ) : objective?.status === 'ERROR' ? (
        <div className="text-center py-12">
          <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <p className="text-sm text-gray-600 dark:text-medical-400">Causation analysis failed during extraction.</p>
        </div>
      ) : (
        <div className="text-center py-12">
          <Link2 className="w-10 h-10 text-gray-300 dark:text-medical-600 mx-auto mb-3" />
          <p className="text-sm text-gray-600 dark:text-medical-400 max-w-md mx-auto">
            The uploaded evidence did not contain sufficient information to generate a causation analysis.
          </p>
        </div>
      )}
    </div>
  );
};
